import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Menu,
  MenuItem,
  Collapse,
  Typography,
  useMediaQuery,
  useTheme,
  Container,
  Grid,
} from "@mui/material";

import MenuIcon from "@mui/icons-material/Menu";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LoginIcon from "@mui/icons-material/Login";

import HomeIcon from "@mui/icons-material/Home";
import DescriptionIcon from "@mui/icons-material/Description";
import BuildIcon from "@mui/icons-material/Build";
import SettingsIcon from "@mui/icons-material/Settings";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import InfoIcon from "@mui/icons-material/Info";
import WebIcon from "@mui/icons-material/Web";
import CloudIcon from "@mui/icons-material/Cloud";
import DeveloperBoardIcon from "@mui/icons-material/DeveloperBoard";
import CallMissedIcon from "@mui/icons-material/CallMissed";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import RouterIcon from "@mui/icons-material/Router";
import SecurityIcon from "@mui/icons-material/Security";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ScienceIcon from "@mui/icons-material/Science";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import StorageIcon from "@mui/icons-material/Storage";
import BugReportIcon from "@mui/icons-material/BugReport";
import ComputerIcon from "@mui/icons-material/Computer";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import GroupsIcon from "@mui/icons-material/Groups";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import SchoolIcon from "@mui/icons-material/School";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PsychologyIcon from "@mui/icons-material/Psychology";
import ChildCareIcon from "@mui/icons-material/ChildCare";

const Navbar = ({ contactRef }) => {
  // =========================================================
  // STATE
  // =========================================================

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorElProducts, setAnchorElProducts] = useState(null);
  const [anchorElServices, setAnchorElServices] = useState(null);
  const [impulzAnchorEl, setImpulzAnchorEl] = useState(null);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileImpulzOpen, setMobileImpulzOpen] = useState(false);

  const impulzCloseTimer = useRef(null);
  const navigate = useNavigate();
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // =========================================================
  // HANDLERS
  // =========================================================

  const handleLoginClick = () => {
    setMobileOpen(false);
    window.location.href = "/login";
  };

  const handleLogoDoubleTap = () => {
    navigate("/");
  };

  const openItem = (item) => {
    if (item.externalUrl) {
      window.open(item.externalUrl, "_blank", "noopener,noreferrer");
    } else if (item.path) {
      navigate(item.path);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMobileProductsToggle = () => {
    setMobileProductsOpen(!mobileProductsOpen);
    if (mobileProductsOpen) {
      setMobileImpulzOpen(false);
    }
  };

  const handleMobileServicesToggle = () => {
    setMobileServicesOpen(!mobileServicesOpen);
  };

  // =========================================================
  // IMPULZ DESKTOP HOVER
  // =========================================================

  const openImpulzSubmenu = (event) => {
    if (impulzCloseTimer.current) {
      clearTimeout(impulzCloseTimer.current);
    }
    setImpulzAnchorEl(event.currentTarget);
  };

  const closeImpulzSubmenu = () => {
    if (impulzCloseTimer.current) {
      clearTimeout(impulzCloseTimer.current);
    }
    impulzCloseTimer.current = setTimeout(() => {
      setImpulzAnchorEl(null);
    }, 180);
  };

  const keepImpulzSubmenuOpen = () => {
    if (impulzCloseTimer.current) {
      clearTimeout(impulzCloseTimer.current);
    }
  };

  // =========================================================
  // NAV ITEMS
  // =========================================================

  const navItems = [
    { text: "Home", path: "/", hasDropdown: false, icon: <HomeIcon /> },
    { text: "About Us", path: "/about", hasDropdown: false, icon: <InfoIcon /> },
    { text: "Products", hasDropdown: true, icon: <BuildIcon /> },
    { text: "Services", hasDropdown: true, icon: <SettingsIcon /> },
    { text: "Careers", path: "/careers", hasDropdown: false, icon: <WorkOutlineIcon /> },
    { text: "Newsroom", path: "/newsletter", hasDropdown: false, icon: <NewspaperIcon /> },
    { text: "Our Clients", path: "/client", hasDropdown: false, icon: <GroupsIcon /> },
  ];

  // =========================================================
  // PRODUCTS
  // =========================================================

  const productsItems = [
    {
      text: "ImpulZ",
      icon: <SchoolIcon />,
      hasSubMenu: true,
      children: [
        {
          text: "ImpulZ School LMS",
          externalUrl: "https://9ggv8r3x-3000.inc1.devtunnels.ms/product/school-lms",
          icon: <SchoolIcon />,
        },
        {
          text: "ImpulZ College LMS",
          externalUrl: "https://9ggv8r3x-3000.inc1.devtunnels.ms/product/college-lms",
          icon: <AccountBalanceIcon />,
        },
        {
          text: "ImpulZ TNPSC",
          externalUrl: "https://9ggv8r3x-3000.inc1.devtunnels.ms/product/tnpsc",
          icon: <AssignmentIcon />,
        },
        {
          text: "ImpulZ JEE / NEET",
          externalUrl: "https://9ggv8r3x-3000.inc1.devtunnels.ms/product/jee-neet",
          icon: <PsychologyIcon />,
        },
        {
          text: "ImpulZ Kids",
          externalUrl: "https://9ggv8r3x-3000.inc1.devtunnels.ms/product/kids",
          icon: <ChildCareIcon />,
        },
      ],
    },
    { text: "Lider Connect", path: "/products/lider-connect", icon: <RouterIcon /> },
    { text: "Lider ERP", path: "/products/lider-erp", icon: <DescriptionIcon /> },
    { text: "Koottu", path: "/products/bower-cred", icon: <AccountBalanceWalletIcon /> },
    { text: "Circuit", path: "/products/circuit", icon: <AssessmentIcon /> },
    { text: "Lider SecureGate", path: "/products/iclever-gate", icon: <SecurityIcon /> },
    { text: "iPathway", path: "/products/pathway", icon: <DeveloperBoardIcon /> },
    { text: "Missed Call Alert", path: "/products/missed-call-alert", icon: <CallMissedIcon /> },
    { text: "Thedal Directory", path: "/products/thedal-dictionary", icon: <MenuBookIcon /> },
    { text: "Kiosk", path: "/products/kiosk", icon: <ComputerIcon /> },
  ];

  // =========================================================
  // SERVICES
  // =========================================================

  const servicesItems = [
    { text: "Website Development", path: "/services/website-development", icon: <WebIcon /> },
    { text: "Software Testing", path: "/services/software-testing", icon: <BugReportIcon /> },
    { text: "Manual Testing", path: "/services/manual-testing", icon: <FactCheckIcon /> },
    { text: "Software Automation Testing", path: "/services/software-automation-testing", icon: <StorageIcon /> },
    { text: "Cloud & Data Analytics", path: "/services/cloud-data-analytics", icon: <CloudIcon /> },
    { text: "App Development", path: "/services/app-development", icon: <SmartphoneIcon /> },
    { text: "R&D Lab", path: "/services/research-and-development", icon: <ScienceIcon /> },
  ];

  // =========================================================
  // SPLIT COLUMNS
  // =========================================================

  const splitIntoColumns = (items) => {
    const mid = Math.ceil(items.length / 2);
    return {
      column1: items.slice(0, mid),
      column2: items.slice(mid),
    };
  };

  const productsColumns = splitIntoColumns(productsItems);
  const servicesColumns = splitIntoColumns(servicesItems);

  // =========================================================
  // STYLES
  // =========================================================

  const menuItemSx = {
    py: 1.5,
    px: 2,
    borderRadius: "8px",
    mb: 0.5,
    "&:hover": {
      backgroundColor: "rgba(25, 118, 210, 0.06)",
    },
  };

  const mobileIconBoxSx = {
    minWidth: 36,
    color: "primary.main",
    background: "rgba(25, 118, 210, 0.08)",
    borderRadius: "8px",
    width: 34,
    height: 34,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    mr: 2,
  };

  const navButtonSx = {
    fontSize: { lg: "14px", xl: "16px" },
    px: { lg: 1.5, xl: 2 },
    py: 1.2,
    borderRadius: "8px",
    textTransform: "none",
    fontWeight: 600,
    color: "text.primary",
    whiteSpace: "nowrap",
  };

  const menuPaperSx = {
    mt: 0.5,
    "& .MuiPaper-root": {
      borderRadius: 2,
      boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
      minWidth: 500,
      maxHeight: 500,
      overflow: "visible",
      border: "1px solid rgba(0,0,0,0.05)",
      background: "#ffffff",
    },
  };

  const menuHeadingSx = {
    px: 3,
    py: 2,
    color: "primary.main",
    fontWeight: 700,
    borderBottom: "2px solid rgba(25, 118, 210, 0.1)",
    mb: 1,
  };

  const mobileRowSx = {
    py: 1.75,
    px: 2.5,
    borderRadius: "12px",
    mb: 1,
    background: "rgba(25, 118, 210, 0.04)",
    border: "1px solid rgba(25, 118, 210, 0.08)",
  };

  const mobileRowIconSx = {
    minWidth: 44,
    color: "primary.main",
    background: "rgba(25, 118, 210, 0.08)",
    borderRadius: "10px",
    width: 40,
    height: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    mr: 2,
  };

  // =========================================================
  // DESKTOP PRODUCT MENU ITEM
  // =========================================================

  const renderProductMenuItem = (subItem) => {
    const isImpulz = subItem.text === "ImpulZ";

    return (
      <Box
        key={subItem.text}
        sx={{ position: "relative" }}
        onMouseEnter={(event) => {
          if (isImpulz) openImpulzSubmenu(event);
        }}
        onMouseLeave={() => {
          if (isImpulz) closeImpulzSubmenu();
        }}
      >
        <MenuItem
          onClick={() => {
            if (!isImpulz) {
              setAnchorElProducts(null);
              openItem(subItem);
            }
          }}
          sx={{
            ...menuItemSx,
            minWidth: 210,
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
            <ListItemIcon sx={{ minWidth: 40, color: "primary.main" }}>
              {subItem.icon}
            </ListItemIcon>
            <Typography variant="body1" fontWeight={isImpulz ? 600 : 500}>
              {subItem.text}
            </Typography>
          </Box>
          {isImpulz && (
            <ArrowDropDownIcon
              sx={{ fontSize: 20, color: "text.secondary", transform: "rotate(-90deg)" }}
            />
          )}
        </MenuItem>

        {isImpulz && (
          <Box
            onMouseEnter={keepImpulzSubmenuOpen}
            onMouseLeave={closeImpulzSubmenu}
            sx={{
              position: "absolute",
              top: 0,
              right: "calc(100% + 4px)",
              left: "auto",
              width: 310,
              backgroundColor: "#ffffff",
              borderRadius: "10px",
              boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
              border: "1px solid rgba(0,0,0,0.06)",
              zIndex: 1600,
              p: 1.2,
              display: impulzAnchorEl === null ? "none" : "block",
            }}
          >
            {subItem.children.map((child) => (
              <MenuItem
                key={child.text}
                onClick={() => {
                  setAnchorElProducts(null);
                  setImpulzAnchorEl(null);
                  openItem(child);
                }}
                sx={{
                  py: 1.5,
                  px: 2,
                  borderRadius: "8px",
                  mb: 0.5,
                  "&:hover": { backgroundColor: "rgba(25, 118, 210, 0.08)" },
                }}
              >
                <ListItemIcon sx={{ minWidth: 42, color: "#2563eb" }}>
                  {child.icon}
                </ListItemIcon>
                <Typography variant="body1" sx={{ color: "#374151", fontWeight: 500, fontSize: "15px" }}>
                  {child.text}
                </Typography>
              </MenuItem>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  // =========================================================
  // DESKTOP SERVICE MENU ITEM
  // =========================================================

  const renderServiceMenuItem = (subItem) => (
    <MenuItem
      key={subItem.text}
      onClick={() => {
        setAnchorElServices(null);
        navigate(subItem.path);
      }}
      sx={menuItemSx}
    >
      <ListItemIcon sx={{ minWidth: 40, color: "primary.main" }}>
        {subItem.icon}
      </ListItemIcon>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body1" fontWeight={500}>
          {subItem.text}
        </Typography>
      </Box>
    </MenuItem>
  );

  // =========================================================
  // MOBILE NORMAL SUB ITEM
  // =========================================================

  const renderMobileSubItem = (subItem) => {
    const isImpulz = subItem.text === "ImpulZ";

    if (isImpulz) {
      return (
        <React.Fragment key={subItem.text}>
          <ListItem
            button
            onClick={() => setMobileImpulzOpen(!mobileImpulzOpen)}
            sx={{ pl: 3.5, py: 1.5, borderRadius: "10px", mb: 0.5 }}
          >
            <ListItemIcon sx={mobileIconBoxSx}>{subItem.icon}</ListItemIcon>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={600} color="text.primary">
                {subItem.text}
              </Typography>
            </Box>
            {mobileImpulzOpen ? (
              <ExpandLessIcon sx={{ color: "text.secondary" }} />
            ) : (
              <ExpandMoreIcon sx={{ color: "text.secondary" }} />
            )}
          </ListItem>

          <Collapse in={mobileImpulzOpen} timeout="auto" unmountOnExit>
            <Box
              sx={{
                ml: 3,
                mr: 1,
                mb: 1,
                background: "#ffffff",
                borderRadius: "10px",
                border: "1px solid rgba(25, 118, 210, 0.12)",
                boxShadow: "0 4px 15px rgba(0,0,0,0.06)",
                p: 0.8,
              }}
            >
              {subItem.children.map((child) => (
                <ListItem
                  button
                  key={child.text}
                  onClick={() => {
                    setMobileOpen(false);
                    setMobileProductsOpen(false);
                    setMobileImpulzOpen(false);
                    openItem(child);
                  }}
                  sx={{
                    py: 1.3,
                    px: 1.5,
                    borderRadius: "8px",
                    mb: 0.3,
                    "&:hover": { backgroundColor: "rgba(25, 118, 210, 0.08)" },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: "#2563eb" }}>
                    {child.icon}
                  </ListItemIcon>
                  <Typography variant="body2" sx={{ color: "#374151", fontWeight: 500 }}>
                    {child.text}
                  </Typography>
                </ListItem>
              ))}
            </Box>
          </Collapse>
        </React.Fragment>
      );
    }

    return (
      <ListItem
        button
        key={subItem.text}
        sx={{ pl: 3.5, py: 1.5, borderRadius: "10px", mb: 0.5 }}
        onClick={() => {
          handleDrawerToggle();
          openItem(subItem);
        }}
      >
        <ListItemIcon sx={mobileIconBoxSx}>{subItem.icon}</ListItemIcon>
        <Box>
          <Typography variant="body2" fontWeight={500} color="text.primary">
            {subItem.text}
          </Typography>
        </Box>
      </ListItem>
    );
  };

  // =========================================================
  // RETURN
  // =========================================================

  return (
    <>
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          background: "#dbeeff",
          borderBottom: "1px solid rgba(25, 118, 210, 0.18)",
          boxShadow: "0 2px 8px rgba(25, 118, 210, 0.08)",
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Toolbar
            disableGutters
            sx={{
              minHeight: { xs: 60, sm: 70 },
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            {/* =================================================
                LOGO - RESPONSIVE FIX
            ================================================= */}
            <Box
              onClick={handleLogoDoubleTap}
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                userSelect: "none",
                flexShrink: 0,
                // Removed the negative margins that broke responsiveness
                // Use maxWidth to prevent overflow
                maxWidth: { xs: "120px", sm: "150px", md: "180px", lg: "200px" },
              }}
            >
              <Box
                component="img"
                src="/images/lider_new2.png"
                alt="Lider Logo"
                draggable={false}
                sx={{
                  height: { xs: 32, sm: 38, md: 44, lg: 48 },
                  width: "auto",
                  maxWidth: "100%",
                  objectFit: "contain",
                  display: "block",
                  userSelect: "none",
                  WebkitUserSelect: "none",
                  WebkitTouchCallout: "none",
                }}
              />
            </Box>

            {/* =================================================
                DESKTOP NAVIGATION
            ================================================= */}
            {!isMobile && (
              <Box
                sx={{
                  display: "flex",
                  gap: { lg: 0.5, xl: 1 },
                  alignItems: "center",
                  flex: 1,
                  justifyContent: "flex-end",
                }}
              >
                {navItems.map((item) => (
                  <Box
                    key={item.text}
                    onMouseEnter={(e) => {
                      if (item.text === "Products") setAnchorElProducts(e.currentTarget);
                      if (item.text === "Services") setAnchorElServices(e.currentTarget);
                    }}
                    onMouseLeave={() => {
                      if (item.text === "Services") setAnchorElServices(null);
                    }}
                  >
                    {item.hasDropdown ? (
                      <>
                        <Button
                          color="inherit"
                          sx={navButtonSx}
                          endIcon={
                            <ArrowDropDownIcon sx={{ color: "text.secondary", fontSize: 18 }} />
                          }
                        >
                          {item.text}
                        </Button>

                        {item.text === "Products" && (
                          <Menu
                            anchorEl={anchorElProducts}
                            open={Boolean(anchorElProducts)}
                            onClose={() => {
                              setAnchorElProducts(null);
                              setImpulzAnchorEl(null);
                            }}
                            MenuListProps={{
                              onMouseEnter: () => keepImpulzSubmenuOpen(),
                              sx: { py: 1 },
                            }}
                            sx={menuPaperSx}
                          >
                            <Typography variant="subtitle2" sx={menuHeadingSx}>
                              OUR PRODUCTS
                            </Typography>
                            <Box sx={{ px: 2, py: 1 }}>
                              <Grid container spacing={2}>
                                <Grid item xs={6}>
                                  {productsColumns.column1.map(renderProductMenuItem)}
                                </Grid>
                                <Grid item xs={6}>
                                  {productsColumns.column2.map(renderProductMenuItem)}
                                </Grid>
                              </Grid>
                            </Box>
                          </Menu>
                        )}

                        {item.text === "Services" && (
                          <Menu
                            anchorEl={anchorElServices}
                            open={Boolean(anchorElServices)}
                            onClose={() => setAnchorElServices(null)}
                            MenuListProps={{
                              onMouseLeave: () => setAnchorElServices(null),
                              sx: { py: 1 },
                            }}
                            sx={menuPaperSx}
                          >
                            <Typography variant="subtitle2" sx={menuHeadingSx}>
                              OUR SERVICES
                            </Typography>
                            <Box sx={{ px: 2, py: 1 }}>
                              <Grid container spacing={2}>
                                <Grid item xs={6}>
                                  {servicesColumns.column1.map(renderServiceMenuItem)}
                                </Grid>
                                <Grid item xs={6}>
                                  {servicesColumns.column2.map(renderServiceMenuItem)}
                                </Grid>
                              </Grid>
                            </Box>
                          </Menu>
                        )}
                      </>
                    ) : (
                      <Button
                        color="inherit"
                        onClick={() => navigate(item.path)}
                        sx={navButtonSx}
                      >
                        {item.text}
                      </Button>
                    )}
                  </Box>
                ))}

                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<LoginIcon />}
                  onClick={handleLoginClick}
                  sx={{
                    ml: { lg: 1, xl: 2 },
                    px: { lg: 2, xl: 3 },
                    py: 1,
                    borderRadius: "10px",
                    textTransform: "none",
                    fontSize: { lg: "14px", xl: "15px" },
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    boxShadow: "0 4px 12px rgba(25,118,210,0.25)",
                    "&:hover": {
                      boxShadow: "0 6px 16px rgba(25,118,210,0.35)",
                    },
                  }}
                >
                  Login
                </Button>
              </Box>
            )}

            {/* =================================================
                MOBILE MENU BUTTON
            ================================================= */}
            {isMobile && (
              <IconButton
                edge="end"
                color="inherit"
                aria-label="menu"
                onClick={handleDrawerToggle}
                sx={{
                  color: "primary.main",
                  backgroundColor: "rgba(25, 118, 210, 0.08)",
                  borderRadius: "10px",
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* =====================================================
          MOBILE DRAWER
      ===================================================== */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: 280, sm: 300 },
            top: 0,
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            background: "#ffffff",
          },
        }}
      >
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* DRAWER HEADER */}
          <Box
            sx={{
              px: 3,
              pt: 5,
              pb: 3,
              borderBottom: "1px solid rgba(25, 118, 210, 0.18)",
              background: "#dbeeff",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Box
                component="img"
                src="/images/lider_new2.png"
                alt="Lider Logo"
                draggable={false}
                onClick={handleLogoDoubleTap}
                sx={{
                  height: 45,
                  maxWidth: "100%",
                  cursor: "pointer",
                  userSelect: "none",
                  WebkitUserSelect: "none",
                  WebkitTouchCallout: "none",
                }}
              />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                fontSize: "1.15rem",
                letterSpacing: "1.5px",
                textTransform: "uppercase",
                color: "#1976d2",
                lineHeight: 1.3,
              }}
            >
              LIDER TECHNOLOGY
            </Typography>
          </Box>

          {/* MOBILE NAVIGATION */}
          <List sx={{ flexGrow: 1, overflowY: "auto", px: 2, py: 2 }}>
            {navItems.map((item) => {
              const isProducts = item.text === "Products";
              const isOpen = isProducts ? mobileProductsOpen : mobileServicesOpen;

              return (
                <React.Fragment key={item.text}>
                  {item.hasDropdown ? (
                    <>
                      <ListItem
                        button
                        onClick={
                          isProducts ? handleMobileProductsToggle : handleMobileServicesToggle
                        }
                        sx={mobileRowSx}
                      >
                        <ListItemIcon sx={mobileRowIconSx}>{item.icon}</ListItemIcon>
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{
                            fontWeight: 600,
                            color: "text.primary",
                            fontSize: "1.05rem",
                          }}
                        />
                        {isOpen ? (
                          <ExpandLessIcon sx={{ color: "text.secondary" }} />
                        ) : (
                          <ExpandMoreIcon sx={{ color: "text.secondary" }} />
                        )}
                      </ListItem>

                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                          <Box
                            sx={{
                              background: "rgba(0,0,0,0.02)",
                              borderRadius: "12px",
                              p: 1,
                              mb: 2,
                              border: "1px solid rgba(0,0,0,0.05)",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                px: 2.5,
                                py: 1,
                                color: "primary.main",
                                fontWeight: 700,
                                textTransform: "uppercase",
                                letterSpacing: "1px",
                                display: "block",
                              }}
                            >
                              {isProducts ? "Products" : "Services"}
                            </Typography>
                            {(isProducts ? productsItems : servicesItems).map(
                              renderMobileSubItem
                            )}
                          </Box>
                        </List>
                      </Collapse>
                    </>
                  ) : (
                    <ListItem
                      button
                      onClick={() => {
                        handleDrawerToggle();
                        navigate(item.path);
                      }}
                      sx={mobileRowSx}
                    >
                      <ListItemIcon sx={mobileRowIconSx}>{item.icon}</ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          fontWeight: 600,
                          color: "text.primary",
                          fontSize: "1.05rem",
                        }}
                      />
                    </ListItem>
                  )}
                </React.Fragment>
              );
            })}
          </List>

          {/* MOBILE FOOTER + LOGIN */}
          <Box
            sx={{
              px: 3,
              py: 3,
              borderTop: "1px solid rgba(25, 118, 210, 0.18)",
              background: "rgba(219, 238, 255, 0.6)",
            }}
          >
            <Button
              fullWidth
              variant="contained"
              color="primary"
              startIcon={<LoginIcon />}
              onClick={handleLoginClick}
              sx={{
                mb: 2,
                py: 1.3,
                borderRadius: "10px",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: 700,
              }}
            >
              Login
            </Button>
            <Typography variant="body2" color="text.primary" sx={{ mb: 0.5, fontWeight: 500 }}>
              © {new Date().getFullYear()} Lider Technology
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Lider Technology Solutions
            </Typography>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;
